import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { processAppData } from '@Core/API/Data/AppDataProcessor';
import { isDoubleLockAndRollCoinPresent } from '@Core/API/Data/DoubleLockAndRollCoinDataProcessor';
import { processActionResponse } from '@Core/API/Responses/ActionResponseProcessor';
import { processAuthenticateResponse } from '@Core/API/Responses/AuthenticateResponseProcessor';
import { BaseGameModifier } from '@Core/Events/EventModifiers/BaseGameModifier';
import { FreeGamesModifier } from '@Core/Events/EventModifiers/FreeGamesModifier';
import { LockAndRollModifier } from '@Core/Events/EventModifiers/LockAndRollModifier';
import { IModifier } from '@Core/Events/EventModifiers/Modifier';
import { OfAKindModifier } from '@Core/Events/EventModifiers/OfAKindModifier';
import { OrientationModifier } from '@Core/Events/EventModifiers/OrientationModifier';
import { SymbolCountModifier } from '@Core/Events/EventModifiers/SymbolCountModifier';
import { WallMultiplierModifier } from '@Core/Events/EventModifiers/WallMultiplierModifier';
import { GameScene, ReelsBackground } from '@Core/index';
import { IPreloaderState } from '@Core/PreloaderScene/PreloaderScene.types';
import { ReelTensionManager } from '@Core/Reels/ReelTensionManager';
import { ESymbolAnimations } from '@Core/Reels/SymbolAnimations';
import { isBeforeFirstLockAndRollSpin, isLastSpecialGamesSpin } from '@Core/SpecialGames/SpecialGamesHelper';
import DataManager from '@DataManager/DataManager';
import { GameHistory } from '@GameHistory/GameHistory';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys, ISoundSettings } from '@GameStore/GameStoreSchema';
import { ESpecialGamesType, TJackpotCasinoEventTypes, TJackpotCasinoNotification } from '@GFTypes/index';
import { IActionResponseData } from '@GFTypes/Responses/ActionResponse';
import { IAuthenticateResponseData } from '@GFTypes/Responses/AuthenticateResponse';
import { AutoPlayManager } from '@Managers/AutoPlayManager/AutoPlayManager';
import { BetManager } from '@Managers/Bet/BetManager';
import { ButtonManager } from '@Managers/ButtonManager/ButtonManager';
import { reMobileDevice, reMobileDevice2 } from '@Managers/Device/UseWindowDimensions.types';
import { EActionName } from '@Managers/Events/EActionName';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStoreEvents } from '@Managers/Events/GameStoreEvents';
import { FreeSpinsManager } from '@Managers/FreeSpinsManager/FreeSpinsManager';
import { InputManager } from '@Managers/InputManager/InputManager';
import { Logger } from '@Managers/Logger';
import { ReelSymbolsSetManager } from '@Managers/ReelSetManager/ReelSetManager';
import { RestoreManager } from '@Managers/RestoreManager/RestoreManager';
import { ScaleManager } from '@Managers/ScaleManager/ScaleManager';
import { SoundManager } from '@Managers/SoundManager/SoundManager';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateManager } from '@Managers/StateManager/StateManager';
import LocalStorageItem from '@Managers/Storage/LocalStorageItem';
import LocalStorageManager from '@Managers/Storage/LocalStorageManager';
import { LocalStorageItemKeys } from '@Managers/Storage/LocalStorageManager.types';
import { TransactionCycleManager } from '@Managers/TransactionCycleManager/TransactionCycleManager';
import { TurboManager } from '@Managers/TurboManager/TurboManager';
import { initDevtools } from '@pixi/devtools';
import { getDevicePixelRatio } from '@Tools/DevicePixelRatio';
import paramToObject from '@Tools/ParamToObject';
import gsap from 'gsap';
import { PixiPlugin } from 'gsap/PixiPlugin';
import { Application, Renderer, Ticker, UPDATE_PRIORITY } from 'pixi.js';
import { FPSTracker } from '../FPSTracker/FPSTracker';

export class Game {
    public renderer: Application<Renderer>;
    public gameTicker: Ticker;
    public fpsTracker: FPSTracker;
    public gameStore: GameStore;
    public spines: SpineWrapper[];
    public globalEventModifiers: IModifier[];
    public buttonManager: ButtonManager;
    public inputManager: InputManager;
    public betManager: BetManager;
    public reelSymbolsSetManager: ReelSymbolsSetManager;
    public soundManager: SoundManager;
    public scaleManager: ScaleManager;
    public autoPlayManager: AutoPlayManager;
    public freeGamesAutoPlayManager: AutoPlayManager;
    public freeSpinsManager: FreeSpinsManager;
    public turboManager: TurboManager;
    public transactionCycleManager: TransactionCycleManager;
    public gameHistory: GameHistory;
    public restoreManager: RestoreManager;
    public reelTensionManager: ReelTensionManager;
    private registry: DataManager;
    public gameScene: GameScene;
    constructor(renderer: Application<Renderer>) {
        this.renderer = renderer;
        this.fpsTracker = new FPSTracker(100);
        this.registry = new DataManager(this, EventsCenter);
        this.gameStore = new GameStore(this.registry);
        this.gameStore.set(EGameStoreKeys.DevicePixelRatio, getDevicePixelRatio());
        const isMobile = reMobileDevice.test(navigator.userAgent) || reMobileDevice2.test(navigator.userAgent.substr(0, 4));
        this.gameStore.set(EGameStoreKeys.IsMobile, isMobile);
        this.spines = [];
        this.globalEventModifiers = [
            new BaseGameModifier(this.gameStore),
            new LockAndRollModifier(this.gameStore),
            new FreeGamesModifier(this.gameStore),
            new OfAKindModifier(this.gameStore),
            new OrientationModifier(this.gameStore),
            new WallMultiplierModifier(this.gameStore),
            new SymbolCountModifier(this.gameStore)
        ];
        this.buttonManager = new ButtonManager(this);
        const inputConfig = ConfigLoader.getConfiguration(EConfigurationNames.GAME).input;
        this.inputManager = new InputManager(this, inputConfig);
        this.reelSymbolsSetManager = new ReelSymbolsSetManager(this);
        this.betManager = new BetManager(this.gameStore);
        this.soundManager = new SoundManager(this);
        this.scaleManager = new ScaleManager(this.gameStore);
        this.autoPlayManager = new AutoPlayManager(this, EGameStoreKeys.AutoPlayState);
        this.freeGamesAutoPlayManager = new AutoPlayManager(this, EGameStoreKeys.FreeGamesAutoPlayState);
        this.freeSpinsManager = new FreeSpinsManager(this);
        this.turboManager = new TurboManager(this);
        this.transactionCycleManager = new TransactionCycleManager(this);
        this.gameHistory = new GameHistory(this);
        this.restoreManager = new RestoreManager(this);
        this.reelTensionManager = new ReelTensionManager(this);

        const { kibanaConfiguration: kibana } = ConfigLoader.getConfiguration(EConfigurationNames.CONNECTION);
        Logger.init(paramToObject(), kibana);

        EventsCenter.on(EGameEvents.GAME_INSTANCE_READY, this.onGameInstanceReady, this);
        EventsCenter.on(
            EGameEvents.ACTION_RESPONSE_RECEIVED,
            ({ response, fromHistory }: { response: IActionResponseData & { actionName: EActionName }; fromHistory?: boolean }) =>
                this.onActionResultReceived(response, fromHistory)
        );
        EventsCenter.on(EGameEvents.REFRESH_BALANCE_RECEIVED, this.onRefreshBalanceReceived, this);
        EventsCenter.on(EGameStates.SPIN_STATE, this.onSpinStart, this);
        EventsCenter.on(EGameStates.WIN_STATE, this.onWinStart, this);
        EventsCenter.on(EGameStoreEvents.GAME_STORE_SOUND_STATE_UPDATE, this.onSoundSettingsUpdate, this);
        EventsCenter.on(EGameStoreEvents.GAME_STORE_PRELOADER_STATE_UPDATE, this.onPreloadeStateUpdate, this);
        EventsCenter.on(EGameEvents.INITIATE_PAUSE, this.pauseGame, this);
        EventsCenter.on(EGameEvents.INITIATE_UNPAUSE, this.unpauseGame, this);
        EventsCenter.on(EGameEvents.JACKPOT_CASINO_NOTIFICATION, this.onJackpotCasinoNotification, this);
        EventsCenter.on(EGameEvents.CHANGE_REEL_COUNT, this.changeReelCount, this);
        EventsCenter.on(EGameEvents.SPECIAL_GAMES_RESTORE, this.restoreToStartedLR, this);
    }

    public onResize() {
        if (!this.gameScene) return;
        this.scaleManager.onResize(this.gameScene);
    }

    public initPIXI = async () => {
        gsap.registerPlugin(PixiPlugin);
        this.gameScene = new GameScene(this);
        this.gameTicker = new Ticker();
        this.gameTicker.autoStart = false;

        const FIXED_DT_MS = 1000 / 60;
        const MAX_FRAME_TIME_MS = 250;
        let accumulator = 0;

        this.gameTicker.add(
            (ticker) => {
                this.fpsTracker.push(ticker.deltaMS);

                let frameTime = ticker.deltaMS;
                frameTime = Math.min(frameTime, MAX_FRAME_TIME_MS);
                accumulator += frameTime;

                while (accumulator >= FIXED_DT_MS) {
                    this.gameScene.update(FIXED_DT_MS);

                    for (const spine of this.spines) {
                        if (spine.spine.visible) spine.spine.update(FIXED_DT_MS / 1000);
                    }

                    accumulator -= FIXED_DT_MS;
                }

                this.renderer.render();
            },
            this,
            UPDATE_PRIORITY.NORMAL
        );

        this.gameTicker.start();

        initDevtools({ app: this.renderer });
    };

    public initiateStateManager = async (): Promise<void> => {
        StateManager.setGameState(EGameStates.GAME_START);
    };

    public onGameInstanceReady(data: IAuthenticateResponseData): void {
        processAuthenticateResponse(data, this.gameStore);
    }

    public onRefreshBalanceReceived(data: IActionResponseData & { actionName: EActionName }): void {
        processAppData(data, this.gameStore);
        EventsCenter.emit(EGameEvents.REFRESH_BALANCE_PROCESSED, { actionName: data.actionName });
    }

    public onJackpotCasinoNotification(data: TJackpotCasinoNotification) {
        switch (data.event) {
            case TJackpotCasinoEventTypes.JACKPOT_ENABLED:
                this.gameStore.set(EGameStoreKeys.JackpotCasinoState, { enabled: true }, true);
                break;
            case TJackpotCasinoEventTypes.JACKPOT_DISABLED:
                this.gameStore.set(EGameStoreKeys.JackpotCasinoState, { enabled: false }, true);
                break;
            case TJackpotCasinoEventTypes.JACKPOT_STATUS:
                this.gameStore.set(EGameStoreKeys.JackpotCasinoState, { statusLevels: data.status.levels }, true);
                break;
            case TJackpotCasinoEventTypes.JACKPOT_HIT:
                this.gameStore.set(EGameStoreKeys.JackpotCasinoState, { hitsLevels: data.hit.levels }, true);
                break;
            default:
                break;
        }
    }

    public onActionResultReceived(data: IActionResponseData & { actionName: EActionName }, fromHistory?: boolean): void {
        if (this.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode && !fromHistory) {
            this.gameHistory.startReplay();
        } else {
            this.gameHistory.saveAction(data);
            processAppData(data, this.gameStore);

            if (data && !data.error) {
                processActionResponse(data, this.gameStore, this);
            }
        }
    }

    public changeReelCount(): void {
        setTimeout(() => {
            let visSym = this.gameStore.get(EGameStoreKeys.VisibleSymbols);

            visSym.forEach((reel) => {
                reel.forEach((sym) => {
                    sym.stopAnimation();
                });
            });

            const toBaseGame = isLastSpecialGamesSpin(this.gameStore);
            const isGameRestored = this.gameStore.get(EGameStoreKeys.IsGameRestored);
            const outcome = isGameRestored ? this.gameStore.get(EGameStoreKeys.ResumedSpinOutcome)! : this.gameStore.get(EGameStoreKeys.SpinOutcome);
            const doubleReels = outcome.steps.at(0)!.finalSymbolsWithDoubleReels!;
            const mergedReels = outcome.steps.at(0)!.finalSymbolsWithMergedReels!;
            const newFinalSymbols = toBaseGame ? mergedReels : doubleReels;
            if (toBaseGame) this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, undefined);
            this.gameStore.set(EGameStoreKeys.NumberOfReels, newFinalSymbols.length);
            this.gameStore.set(EGameStoreKeys.NumberOfSymbolsPerReel, newFinalSymbols[0].length - 2);
            this.reelSymbolsSetManager.setReelSets(
                toBaseGame
                    ? this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).spinningReels
                    : this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).doubleSpinningReels
            );

            this.reelSymbolsSetManager.setDefaultLockAndRollSets();
            this.reelSymbolsSetManager.recreateLockAndRollSet();
            this.scaleManager.updateReelSlotPrefix(toBaseGame ? ReelsBackground.REEL_SLOT_PREFIX : ReelsBackground.ALTERNATIVE_SLOT_PREFIX);
            this.gameScene.reels.destroyReels();
            this.scaleManager.updateReelsTransform(this.gameScene, true);
            this.gameScene.reels.createReels(newFinalSymbols);
            this.gameScene.stickySymbols.onCreate();
            visSym = this.gameStore.get(EGameStoreKeys.VisibleSymbols);

            visSym.forEach((reel) => {
                reel.forEach((sym) => {
                    if (!toBaseGame) sym.darkenNormalSymbolInLockAndRoll();
                    sym.setLockAndRollCoinValue(true);
                    if (toBaseGame) sym.symbolAnimation.play(ESymbolAnimations.DEFAULT);
                });
            });
            if (toBaseGame) this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, ESpecialGamesType.LOCK_AND_ROLL);
        }, 0);
    }

    public restoreToStartedLR(): void {
        const authGameConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
        const doubleReels = isDoubleLockAndRollCoinPresent(authGameConfig);
        if (!doubleReels) return;

        const isBefore = isBeforeFirstLockAndRollSpin(this.gameStore);

        if (isBefore) {
            this.reelSymbolsSetManager.changeLockAndRollSets();
        } else {
            const toBaseGame = isLastSpecialGamesSpin(this.gameStore);
            this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, toBaseGame ? undefined : ESpecialGamesType.LOCK_AND_ROLL);
            this.scaleManager.updateReelSlotPrefix(toBaseGame ? ReelsBackground.REEL_SLOT_PREFIX : ReelsBackground.ALTERNATIVE_SLOT_PREFIX);

            setTimeout(() => {
                const visSym = this.gameStore.get(EGameStoreKeys.VisibleSymbols);

                visSym.forEach((reel) => {
                    reel.forEach((sym) => {
                        if (!toBaseGame) sym.darkenNormalSymbolInLockAndRoll();
                        sym.setLockAndRollCoinValue(true);
                        sym.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
                    });
                });

                if (toBaseGame) this.gameStore.set(EGameStoreKeys.CurrentSpecialGamesType, ESpecialGamesType.LOCK_AND_ROLL);
            }, 0);
        }
    }

    public onSpinStart(): void {
        if (this.gameStore.get(EGameStoreKeys.GameHistoryState).isHistoryMode)
            setTimeout(() => {
                EventsCenter.emit(EGameEvents.ACTION_RESPONSE_RECEIVED, undefined);
            }, GameHistory.RESPONSE_SPIN_TIME);
        else if (this.gameStore.get(EGameStoreKeys.IsTakeCompleted)) {
            this.gameStore.set(EGameStoreKeys.GeneralAppState, {
                insufficientFunds: !this.gameStore.get(EGameStoreKeys.BetState).available && this.betManager.getCurrentSpinBet() > 0
            });
            this.sendSpinRequest();
        }
    }

    private sendSpinRequest(): void {
        if (!this.gameStore.get(EGameStoreKeys.BonusBuy).isBonusBuyRound) {
            EventsCenter.emit(EGameEvents.SEND_ACTION_REQUEST, {
                actionName: EActionName.SPIN,
                bet: this.betManager.getCurrentSpinBet()
            });
        } else {
            this.gameStore.set(EGameStoreKeys.BonusBuy, { isBonusBuyRound: false }, true);
            EventsCenter.emit(EGameEvents.SEND_ACTION_REQUEST, {
                actionName: EActionName.BONUS_BUY_SPIN,
                bet: this.gameStore.get(EGameStoreKeys.BonusBuy).bonusBuyBet,
                params: {
                    bonusBuy: [this.gameStore.get(EGameStoreKeys.BonusBuy).bonusOption]
                }
            });
        }
    }

    public onWinStart(): void {
        if (this.gameStore.get(EGameStoreKeys.SpinOutcome).win > 0) this.gameStore.set(EGameStoreKeys.IsTakeCompleted, false);
    }

    public onPreloadeStateUpdate(data: IPreloaderState): void {
        this.gameStore.set(EGameStoreKeys.PreloaderState, { ...data }, true);

        if (LocalStorageItemKeys.SHOW_INTRO_MOVIE in data) {
            LocalStorageManager.saveItem(new LocalStorageItem(LocalStorageItemKeys.SHOW_INTRO_MOVIE, JSON.stringify(data.showIntroMovie)));
        }

        if (LocalStorageItemKeys.SHOW_PRELOADER_INFO in data) {
            LocalStorageManager.saveItem(new LocalStorageItem(LocalStorageItemKeys.SHOW_PRELOADER_INFO, JSON.stringify(data.showPreloaderInfo)));
        }
    }

    public onSoundSettingsUpdate(data: ISoundSettings): void {
        this.gameStore.set(EGameStoreKeys.SoundSettings, { ...data });
    }

    public async pauseGame() {
        const isPauseInitiated = this.gameStore.get(EGameStoreKeys.IsPauseInitiated);
        if (isPauseInitiated) return;
        this.gameStore.set(EGameStoreKeys.IsPauseInitiated, true);

        const isInTransaction = this.gameStore.get(EGameStoreKeys.TransactionCycleStarted);
        if (isInTransaction) {
            const { eventName: firstEvent } = await Promise.race([
                EventsCenter.waitForEvent(EGameEvents.TRANSACTION_CYCLE_END),
                EventsCenter.waitForEvent(EGameEvents.INITIATE_UNPAUSE)
            ]);
            const isUnpausedWhileWaitingForEndOfTransactionCycle = firstEvent === EGameEvents.INITIATE_UNPAUSE;
            if (isUnpausedWhileWaitingForEndOfTransactionCycle) return this.gameStore.set(EGameStoreKeys.IsPauseInitiated, false);
        }

        // this.pause();
        EventsCenter.emit(EGameEvents.PAUSE_STATUS, { pause: true });
        EventsCenter.emit(EGameEvents.PAUSE);
        this.gameStore.set(EGameStoreKeys.IsPauseInitiated, false);
    }

    public unpauseGame() {
        // if (!this.isPaused) return;
        // this.resume();
        this.gameStore.set(EGameStoreKeys.IsPauseInitiated, false);
        EventsCenter.emit(EGameEvents.PAUSE_STATUS, { pause: false });
        EventsCenter.emit(EGameEvents.UNPAUSE);
    }
}
